import type { MouseEvent } from "react";
import { MissionControlCard } from "@/components/mission-control/MissionControlCard";
import { formatRelativeTime } from "@/components/missions/MissionRow";
import type { Audit, GuardianType } from "@/services/api/guardiansv2/types";
import { useOsNavigate } from "@/components/ui/os-layout";
import { RatingBadge } from "../shared/RatingBadge";
import { AuditTypeBadge } from "../shared/AuditTypeBadge";
import { PersonaAvatar } from "../shared/PersonaAvatar";
import { entityDeepLink, entityRefFromAudit } from "../shared/entityDeepLink";
import { useGuardiansAuditLaunch } from "../auditLaunch/GuardiansAuditLaunchContext";
import { isAuditPending } from "../auditLaunch/auditPending";
import { isIssueFirstAudit, auditModeLabel } from "./auditPresentation";

const PERSONAS: GuardianType[] = ["ace", "ivy", "nova"];

export interface AuditCardProps {
  audit: Audit;
  selected?: boolean;
  onClick: () => void;
}

export function AuditCard({ audit, selected, onClick }: AuditCardProps) {
  const osNavigate = useOsNavigate();
  const { isAuditInFlight } = useGuardiansAuditLaunch();
  const pending = isAuditPending(audit, isAuditInFlight(audit._id));
  const issueFirst = isIssueFirstAudit(audit);
  const title = audit.inputs?.summary?.trim() || `${audit.audit_type} audit`;
  const snapshot = audit.screening_snapshot;
  const failedCount = snapshot?.failed_checks?.length ?? 0;
  const ref = entityRefFromAudit(audit);
  const deepLink = ref ? entityDeepLink(ref) : null;

  const openSubject = (e: MouseEvent<HTMLButtonElement>) => {
    e.stopPropagation();
    deepLink?.run({ osNavigate });
  };

  return (
    <MissionControlCard
      selected={selected}
      onClick={onClick}
      data-testid={`audit-card-${audit._id}`}
      title={
        <span className="text-sm font-medium text-slate-100 line-clamp-1">{title}</span>
      }
      titleBadges={
        <span className="inline-flex flex-wrap items-center gap-1.5">
          <AuditTypeBadge
            auditType={audit.audit_type}
            onClick={deepLink ? openSubject : undefined}
          />
          <RatingBadge rating={audit.rating} pending={pending} />
        </span>
      }
      timestamp={
        <span className="text-xs text-slate-400 tabular-nums">
          {formatRelativeTime(audit.updated_at || audit.created_at)}
        </span>
      }
      subtitle={
        <span className="mt-1.5 flex flex-col gap-1.5">
          {issueFirst && (snapshot?.catalogue_key || failedCount > 0) ? (
            <span className="text-xs text-slate-400">
              {auditModeLabel("issue_first")}
              {snapshot?.catalogue_key ? ` · ${snapshot.catalogue_key}` : ""}
              {failedCount > 0 ? ` · ${failedCount} failed check${failedCount === 1 ? "" : "s"}` : ""}
            </span>
          ) : null}
          <span className="inline-flex flex-wrap items-center gap-x-3 gap-y-1.5">
            {PERSONAS.map((persona) => {
            const result = audit.personas[persona];
            const skipped = !result && !audit.personas_completed.includes(persona);
            return (
              <PersonaAvatar
                key={persona}
                persona={persona}
                rating={result?.rating}
                score={result?.score}
                skipped={skipped}
              />
            );
          })}
          </span>
        </span>
      }
    />
  );
}
